import { sessionService } from '@/providers/services/auth/session';
import {AssessmentModel} from '@/models/content/assessment';
import { DEFAULT_IMAGES_PATH } from '@/utils/constants';
import { taxonomySerializer } from './taxonomy';
import { questionSerializer } from './question';
import { gutCourseNames, truncateString } from '@/utils/utils';
import moment from 'moment';

export class AssessmentSerializer {
  private static INSTANCE = new AssessmentSerializer();

  static get instance() {
    return this.INSTANCE;
  }

  public serializeAssessment(assessment: any): AssessmentModel {
    const cdnUrls = sessionService.getCdnUrl();
    const contentCdnUrl = cdnUrls.content_cdn_url;
    const thumbnailUrl = assessment.thumbnail ?
      contentCdnUrl + assessment.thumbnail :
      `${window.location.origin}/` + DEFAULT_IMAGES_PATH.assessment;
    const result: AssessmentModel = {
      id: assessment.id,
      title: assessment.title,
      url: assessment.url,
      questionCount: assessment.questionCount,
      description: assessment.learningObjective,
      format: assessment.format || 'assessment',
      efficacy: assessment.efficacy,
      relevance: assessment.relevance,
      engagement: assessment.engagement,
      standards: taxonomySerializer.normalizeTaxonomyObject(assessment.taxonomy),
      creator: taxonomySerializer.normalizeCreator(assessment.creator),
      thumbnailUrl,
    };
    return result;
  }

  public serializeSignatureAssessment(assessment: any): AssessmentModel {
    const cdnUrls = sessionService.getCdnUrl();
    const contentCdnUrl = cdnUrls.content_cdn_url;
    const thumbnailUrl = assessment.thumbnail ?
      contentCdnUrl + assessment.thumbnail :
      `${window.location.origin}/` + DEFAULT_IMAGES_PATH.assessment;
    const result: AssessmentModel = {
      id: assessment.id,
      title: assessment.title,
      thumbnailUrl,
      format: 'signature-assessment',
    };
    return result;
  }

  public normalizeReadAssessment(assessmentData: any) {
    const serializer = this;
    const cdnUrls = sessionService.getCdnUrl();
    const basePath = cdnUrls.content_cdn_url;
    const appRootPath = window.location.origin; // configuration appRootPath
    const thumbnailUrl = assessmentData.thumbnail
      ? basePath + assessmentData.thumbnail
      : appRootPath + DEFAULT_IMAGES_PATH.assessment;
    const metadata = assessmentData.metadata || {};
    const settings = assessmentData.setting || {};
    const normalizedAssessment = {
        id:
          assessmentData.target_collection_id ||
          assessmentData.suggested_content_id ||
          assessmentData.id,
        pathId: assessmentData.id,
        title: assessmentData.title,
        learningObjectives: assessmentData.learning_objective,
        isVisibleOnProfile:
          typeof assessmentData.visible_on_profile !== 'undefined'
            ? assessmentData.visible_on_profile
            : true,
        children: serializer.normalizeQuestions(assessmentData.question),
        questionCount:
          assessmentData.question_count ||
          (assessmentData.question ? assessmentData.question.length : 0),
        sequence: assessmentData.sequence_id,
        thumbnailUrl,
        classroom_play_enabled:
          settings.classroom_play_enabled !== undefined
            ? settings.classroom_play_enabled
            : true,
        standards: taxonomySerializer.normalizeTaxonomyObject(assessmentData.taxonomy),
        format:
          assessmentData.format ||
          assessmentData.target_content_type ||
          assessmentData.suggested_content_type || 'assessment',
        type: 'assessment',
        url: assessmentData.url,
        ownerId: assessmentData.owner_id,
        metadata,
        audience:
          metadata.audience && metadata.audience.length > 0
            ? metadata.audience
            : [],
        depthOfknowledge:
          metadata.depth_of_knowledge && metadata.depth_of_knowledge.length > 0
            ? metadata.depth_of_knowledge
            : [],
        courseId:
          assessmentData.target_course_id ||
          assessmentData.suggested_course_id ||
          assessmentData.course_id,
        unitId:
          assessmentData.target_unit_id ||
          assessmentData.suggested_unit_id ||
          assessmentData.unit_id,
        lessonId:
          assessmentData.target_lesson_id ||
          assessmentData.suggested_lesson_id ||
          assessmentData.lesson_id,
        collectionSubType:
          assessmentData.target_content_subtype ||
          assessmentData.suggested_content_subtype,
        attempts: settings.attempts_allowed || -1,
        bidirectional: settings.bidirectional_play || false,
        centurySkills:
          metadata['21_century_skills'] &&
          metadata['21_century_skills'].length > 0
            ? metadata['21_century_skills']
            : [],
        gutCodes: assessmentData.gut_codes || [],
      };
    return normalizedAssessment;
  }

  public normalizeQuestions(payload: any) {
    const serializer = this;
    if (Array.isArray(payload)) {
      return payload.map((item, index) => {
        return questionSerializer.normalizeReadQuestion(item, index);
      });
    }
    return [];
  }

  public assessmentPulloutSerializer(assessment: any) {
    let resultSet = {};
    const defaultVectorValue = 0.5;
    if (assessment) {
      resultSet = {
        descriptive: {
          title: assessment.title,
          description: truncateString(assessment.description),
        },

        creation: {
          'Publisher': 'Gooru Org',
          'Publish Status': assessment.publishStatus
            ? 'Published'
            : 'Unpublished',
          'Collaborator': assessment.collaboratorIDs,
          'Created by': assessment.creator.username,
          'Aggregator': assessment.aggregator ? assessment.aggregator : null,
          'Modified by':
            assessment.lastModifiedUser.username || assessment.lastModifiedBy,
          'Date Modified':
            moment(assessment.lastModified).format('MMMM DD, YYYY') || null,
          'License': assessment.license
            ? assessment.license.code
            : 'Public Domain',
        },

        educational: {
          'Audience': assessment.audience,
          'Grade Level': assessment.grade,
          'Learning Objective': assessment.learningObjectives,
          'Editorial Range': assessment.publisherQualityIndicator,
        },

        media: {
          Keywords: assessment.keyPoints,
          Visibility:
            assessment.publishStatus === 'published' ? 'True' : 'False',
        },

        instructional: {
          'Instructional Model': assessment.instructionalModel,
          '21st Century Skills': assessment.skills,
        },

        framework: {
          subject: assessment.taxonomySet.subject,
          course: gutCourseNames(assessment.taxonomySet.course),
          domain: assessment.taxonomySet.domain,
          standard: assessment.standards || [],
        },

        vector: {
          relevance: assessment.relevance || defaultVectorValue,
          engagement: assessment.engagement || defaultVectorValue,
          efficacy: assessment.efficacy || defaultVectorValue,
        },
      };
    }
    return resultSet;
  }

}

export const assessmentSerializer = AssessmentSerializer.instance;
